import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Coerce an arbitrary cell/label value to a display string — the single source of truth so the chart,
 * table, and axis paths render the same value identically. Strings/numbers/booleans stringify directly;
 * a warehouse `Date` gets a stable UTC format (see `formatWarehouseDate`), never a locale `toString()`.
 */
export function coerceLabel(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date) return formatWarehouseDate(value);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- assertion narrows for @typescript-eslint/no-base-to-string
  return String(value as string | number | boolean);
}

/**
 * A warehouse `Date` (revived by the RPC client) was never SQL-formatted — `TO_CHAR`/`FORMAT_*` return
 * strings — so a `Date` instance categorically had no author-chosen format to preserve. Give it the
 * prompt's default: date-only at UTC midnight (a `date` column), else `YYYY-MM-DD HH:mm:ss` (UTC),
 * mirroring what the SQL guidance tells agents to emit. Invalid dates keep JS's `Invalid Date` string
 * rather than blanking the cell. (ACT2-401)
 */
function formatWarehouseDate(value: Date): string {
  if (Number.isNaN(value.getTime())) return String(value);
  const d = dayjs.utc(value);
  const midnight = d.hour() === 0 && d.minute() === 0 && d.second() === 0 && d.millisecond() === 0;
  return midnight ? d.format('YYYY-MM-DD') : d.format('YYYY-MM-DD HH:mm:ss');
}
